import { useState, useEffect, useCallback } from "react";
import { metricsAPI } from "../services/api";

export const useMetricsOverview = (timeRange = "7d") => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await metricsAPI.getOverview(timeRange);
      setData(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch metrics overview",
      );
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return { data, loading, error, refetch: fetchOverview };
};

export const usePlatformMetrics = (platform, timeRange = "7d") => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!platform) return;

    const fetchPlatformMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await metricsAPI.getPlatformMetrics(
          platform,
          timeRange,
        );
        setData(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch platform metrics",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlatformMetrics();
  }, [platform, timeRange]);

  return { data, loading, error };
};

export const useMetricsTimeline = (timeRange = "7d", platforms = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await metricsAPI.getTimeline(timeRange, platforms);
        setData(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch metrics timeline",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [timeRange, platforms]);

  return { data, loading, error };
};

export const usePostingTimes = (timeRange = "7d", platforms = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPostingTimes = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await metricsAPI.getPostingTimes(timeRange, platforms);
        setData(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch posting times",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPostingTimes();
  }, [timeRange, platforms]);

  return { data, loading, error };
};
